import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCurrentCompany } from '@/lib/api/companies';
import { getProductMode } from '@/lib/product-mode';

/**
 * ProductModeGuard - layout route (renders Outlet, NOT children).
 *
 * Nested inside ProtectedRoute so auth + company are already resolved when this runs.
 * Nesting pattern: <Route element={<ProtectedRoute />}><Route element={<ProductModeGuard />}>...
 * Do NOT wrap pages as children: <ProductModeGuard><Page /></ProductModeGuard> renders blank.
 *
 * D-11: feedback-mode users hitting SC-only routes are silently redirected to /feedback in prod.
 * D-12: full-mode users on app.feedback-os.com auto-land in /feedback.
 * D-13: dev-only visible banner before redirect (effect delay 1500ms so banner paints first).
 */

/** SC-only route prefixes: blocked for companies with product_mode='feedback'. */
const FEEDBACK_ALLOWED = [
  '/feedback',
  '/forms',
  '/settings',
  '/onboarding',
  '/auth',
];

export function ProductModeGuard() {
  const { data: company, isLoading } = useCurrentCompany();
  const location = useLocation();
  const navigate = useNavigate();

  const mode = company?.productMode ?? 'full';

  // Is the current path SC-only (not reachable in feedback mode)?
  const isSCOnly = !FEEDBACK_ALLOWED.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // D-11: feedback-mode company trying to access an SC-only route
  const blockFeedback = mode === 'feedback' && isSCOnly;

  // D-12: full-mode company on the feedback shell at root/dashboard auto-lands in /feedback
  const autoLandFull =
    mode === 'full' &&
    getProductMode() === 'feedback' &&
    (location.pathname === '/' || location.pathname === '/dashboard');

  useEffect(() => {
    if (isLoading) return;
    if (blockFeedback) {
      console.warn('[ProductModeGuard] blocked feedback-mode access to ' + location.pathname);
      const delay = import.meta.env.DEV ? 1500 : 0;
      const id = setTimeout(() => navigate('/feedback', { replace: true }), delay);
      return () => clearTimeout(id);
    }
    if (autoLandFull) {
      navigate('/feedback', { replace: true });
    }
  }, [isLoading, blockFeedback, autoLandFull, location.pathname, navigate]);

  // While company is loading, render Outlet immediately.
  // ProtectedRoute already gated auth; company resolves fast.
  if (isLoading) {
    return <Outlet />;
  }

  if (blockFeedback) {
    if (import.meta.env.DEV) {
      // Visible banner while the 1500ms redirect delay counts down.
      // Render only the banner so the SC page does not flash behind it.
      return (
        <div className="fixed inset-x-0 top-0 z-50 bg-surface-overlay text-content-primary px-4 py-2 text-sm">
          ProductModeGuard blocked feedback-mode access to {location.pathname}. Redirecting to /feedback.
        </div>
      );
    }
    // Production: effect redirects with delay 0, render null (silent per D-11).
    return null;
  }

  if (autoLandFull) {
    // Effect redirects immediately, render null while it fires.
    return null;
  }

  return <Outlet />;
}
